import { useState, useEffect } from 'react'
import { apiClient } from '@/services/apiClient'
import { Bot, Save, Loader2 } from 'lucide-react'

interface ModelOption {
  value: string
  label: string
}

const ASSISTENTE_MODELS: ModelOption[] = [
  { value: 'gpt-4o-mini', label: 'GPT-4o Mini — ultra-eco legado' },
  { value: 'gpt-5.4-nano', label: 'GPT-5.4 Nano — econômico' },
  { value: 'gpt-5.4-mini', label: 'GPT-5.4 Mini — default ★' },
  { value: 'gpt-5.3-chat-latest', label: 'GPT-5.3 Instant — upgrade' },
  { value: 'gpt-5.4', label: 'GPT-5.4 — premium' },
]

const AGRONOMO_MODELS: ModelOption[] = [
  { value: 'o4-mini', label: 'o4-mini — reasoning eco' },
  { value: 'o3', label: 'o3 — reasoning intermediário' },
  { value: 'gpt-4o', label: 'GPT-4o — legado multimodal' },
  { value: 'gpt-5.4', label: 'GPT-5.4 — modo técnico leve' },
  { value: 'gpt-5.5', label: 'GPT-5.5 — default ★ flagship' },
]

export default function ChatModels() {
  const [assistenteModel, setAssistenteModel] = useState('gpt-4o-mini')
  const [agronomoModel, setAgronomoModel] = useState('gpt-5.3-chat-latest')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState<string | null>(null)
  const [successMsg, setSuccessMsg] = useState<string | null>(null)

  useEffect(() => {
    loadSettings()
  }, [])

  const loadSettings = async () => {
    try {
      const { data } = await apiClient.get('/auth/settings')
      const settings = data.settings || []

      const assistente = settings.find((s: any) => s.key === 'chat_mode_assistente_model')
      if (assistente) setAssistenteModel(assistente.value)

      const agronomo = settings.find((s: any) => s.key === 'chat_mode_agronomo_model')
      if (agronomo) setAgronomoModel(agronomo.value)
    } catch (error) {
      console.error('Erro ao carregar settings:', error)
    } finally {
      setLoading(false)
    }
  }

  const saveModel = async (key: string, value: string) => {
    setSaving(key)
    setSuccessMsg(null)
    try {
      await apiClient.put(`/auth/settings/${key}`, { value })
      setSuccessMsg(key)
      setTimeout(() => setSuccessMsg(null), 2000)
    } catch (error) {
      console.error('Erro ao salvar modelo:', error)
    } finally {
      setSaving(null)
    }
  }

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Modelos do Chat</h1>
        <p className="text-gray-600">Configure o modelo OpenAI utilizado em cada modo do chat</p>
      </div>

      {/* Modo Conversa (Assistente) */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-blue-50 rounded-lg">
            <Bot className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900">Modo Conversa (Assistente)</h3>
            <p className="text-sm text-gray-500">Modo geral de conversa com o assistente</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={assistenteModel}
            onChange={(e) => setAssistenteModel(e.target.value)}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
          >
            {ASSISTENTE_MODELS.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
          <button
            onClick={() => saveModel('chat_mode_assistente_model', assistenteModel)}
            disabled={saving === 'chat_mode_assistente_model'}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 text-sm"
          >
            {saving === 'chat_mode_assistente_model' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            Salvar
          </button>
        </div>
        {successMsg === 'chat_mode_assistente_model' && (
          <p className="text-sm text-green-600 mt-2">Modelo atualizado com sucesso!</p>
        )}
      </div>

      {/* Modo Técnico */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-green-50 rounded-lg">
            <Bot className="h-5 w-5 text-green-600" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900">Modo Técnico</h3>
            <p className="text-sm text-gray-500">Modo especializado com respostas técnicas aprofundadas</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={agronomoModel}
            onChange={(e) => setAgronomoModel(e.target.value)}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
          >
            {AGRONOMO_MODELS.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
          <button
            onClick={() => saveModel('chat_mode_agronomo_model', agronomoModel)}
            disabled={saving === 'chat_mode_agronomo_model'}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 text-sm"
          >
            {saving === 'chat_mode_agronomo_model' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            Salvar
          </button>
        </div>
        {successMsg === 'chat_mode_agronomo_model' && (
          <p className="text-sm text-green-600 mt-2">Modelo atualizado com sucesso!</p>
        )}
      </div>

      {/* Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          <strong>Nota:</strong> A alteracao do modelo afeta imediatamente todas as novas conversas dos usuarios no modo selecionado.
          Conversas em andamento nao sao afetadas.
        </p>
      </div>
    </div>
  )
}
